/**
 * Lettura dei campi di una riga whatsapp_messages comuni a caricamento dal DB
 * (whatsappDbService) e realtime (useWhatsAppRealtime). Modulo puro, testato in
 * __tests__/messageRowMapping.test.ts.
 *
 * - Stato: le righe WAHA hanno `ack_status` (anche 'played'), le righe dei numeri
 *   Ufficiale solo `status` (CHECK: pending/sent/delivered/read/received/failed).
 * - Metadata: i numeri Ufficiale ci scrivono error, location, contacts, kind.
 */

import type { MessageStatus } from '@/lib/types/whatsappChat';
import { cloudFailureReason } from './cloudErrorMessages';

const VALID_STATUS = new Set<MessageStatus>(['pending', 'sent', 'delivered', 'read', 'played', 'failed']);

function toStatus(value: unknown): MessageStatus | null {
  if (typeof value !== 'string' || !value) return null;
  if (value === 'received') return 'delivered'; // messaggio in arrivo: nessuna spunta da mostrare
  return VALID_STATUS.has(value as MessageStatus) ? (value as MessageStatus) : null;
}

/** Stato del messaggio: ack_status (WAHA), poi status (Ufficiale), altrimenti 'pending'. */
export function dbMessageStatus(row: { ack_status?: unknown; status?: unknown }): MessageStatus {
  // Un fallimento registrato in status vince su un ack_status rimasto 'pending'.
  if (row.status === 'failed') return 'failed';
  return toStatus(row.ack_status) || toStatus(row.status) || 'pending';
}

/** Stato in forma di ack (1 inviato, 2 consegnato, 3 letto) per le spunte della lista chat. */
export function statusToAck(status: unknown): number | undefined {
  switch (status) {
    case 'sent': return 1;
    case 'delivered': return 2;
    case 'read':
    case 'played': return 3;
    default: return undefined;
  }
}

/** metadata della riga come oggetto, oppure undefined. */
export function rowMetadata(row: { metadata?: unknown }): Record<string, unknown> | undefined {
  const m = row.metadata;
  return m && typeof m === 'object' && !Array.isArray(m) ? (m as Record<string, unknown>) : undefined;
}

/** Campi extra del Message: metadata e motivo del fallimento (se c'e' un errore salvato). */
export function rowExtras(row: { metadata?: unknown }): { metadata?: Record<string, unknown>; failureReason?: string } {
  const metadata = rowMetadata(row);
  if (!metadata) return {};
  const failureReason = cloudFailureReason(metadata) ?? undefined;
  return failureReason ? { metadata, failureReason } : { metadata };
}

/** Tipo salvato in DB -> tipo del frontend ('contacts' dei vecchi salvataggi Cloud = 'contact'). */
export function normalizeDbMessageType(type: unknown): string {
  if (typeof type !== 'string' || !type) return 'text';
  if (type === 'contacts') return 'contact';
  return type;
}
